// Carries a kc_action result across the full-page OIDC round-trip via
// sessionStorage (in-memory state doesn't survive the navigation): record the
// initiated action, then on return toast only when Keycloak reports
// kc_action_status=success (not when the user hit "Back").
//
// Storage access is guarded throughout — it throws when the browser blocks
// storage, and that must never break startup or login; it degrades to "no toast".

const PENDING_KEY = "kc_pending_action"
const SUCCESS_KEY = "kc_action_success"

/**
 * Access sessionStorage without letting a blocked-storage SecurityError escape.
 * Returns null when storage is unavailable; callers then simply skip the toast.
 */
export const safeSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** Record, before redirecting to Keycloak, which action the dashboard initiated. */
export const markKcActionPending = (
  action: string,
  storage: Storage | null
): void => {
  try {
    storage?.setItem(PENDING_KEY, action)
  } catch {
    // Storage unavailable — the toast is optional, so continue to login().
  }
}

/**
 * Called once at app bootstrap (before OIDC processes the callback). If Keycloak
 * reports the initiated action succeeded, promote it to a one-shot success flag
 * for the hub to consume. The pending flag is always cleared so an abandoned or
 * failed flow can never leak into a later load.
 */
export const captureKcActionResult = (
  search: string,
  storage: Storage | null
): void => {
  if (!storage) return
  try {
    const status = new URLSearchParams(search).get("kc_action_status")
    const pending = storage.getItem(PENDING_KEY)
    storage.removeItem(PENDING_KEY)

    if (status === "success" && pending) {
      storage.setItem(SUCCESS_KEY, pending)
    }
  } catch {
    // Storage unavailable — skip surfacing the toast; never block startup.
  }
}

/**
 * Read and clear the one-shot success flag. Returns the completed action id, or
 * null when none is pending. Clearing on read makes it fire at most once (so a
 * refresh or StrictMode re-mount cannot repeat the toast).
 */
export const consumeKcActionSuccess = (storage: Storage | null): string | null => {
  if (!storage) return null
  try {
    const action = storage.getItem(SUCCESS_KEY)
    storage.removeItem(SUCCESS_KEY)
    return action
  } catch {
    return null
  }
}
